import { TriangleAlert } from 'lucide-react';
import { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from '@/components/ui';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // In production, send to an error-tracking service like Sentry
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) return fallback;

      return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 p-6 text-center sm:p-8">
          {/* A lucide icon, not the emoji this used to be: the emoji rendered
              differently on every platform and an emoji-free codebase has no
              way to keep it. Sized to match the 5xl text it replaces. */}
          <span className="grid h-16 w-16 place-items-center rounded-2xl bg-warning-tint">
            <TriangleAlert className="h-8 w-8 text-ink/70" />
          </span>
          <h1 className="text-2xl font-semibold text-ink">Something went wrong</h1>
          <p className="max-w-sm break-words text-sm text-ink/60">
            {error?.message ?? 'An unexpected error occurred.'}
          </p>
          <div className="flex gap-3">
            <Button onClick={this.handleReset}>Try again</Button>
            <Button variant="secondary" onClick={() => window.location.assign('/')}>
              Go home
            </Button>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
